import { operationIds } from "@gen/app-access-config";
import {
  AddClientRolesToUserBody,
  AddClientRolesToUserBody2,
  CoreQuery,
  CreateClientInvitationBody,
  CreateClientInvitationBody2,
  type IAccessRole,
  type IClientUserWithRoles,
  type IInvitation,
  InvitationStatus,
  Query,
} from "@gen/app-query";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  type ChipOption,
  createChipMultiSelectEditCellRenderer,
  createChipMultiSelectViewCellRenderer,
  EditableDataGrid,
  type EditableDataGridColDef,
} from "~/components/editable_data_grid";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import {
  useAuthOperations,
  useCanExecuteOperation,
} from "~/contexts/auth_query";
import {
  type ClientUserRoleRow,
  clientUserRoleRowSchema,
} from "~/entities/client_user_role";
import { isNonEmptyString } from "~/utilities/string";

// TODO Generate error code constants from the OpenAPI schema.
const ERROR_CODE_USER_NOT_FOUND = "user_not_found";
const ERROR_CODE_OWNER_ALREADY_ASSIGNED = "owner_already_assigned";

// Only realm admins can grant or revoke the owner role.
const OWNER_ROLE = "owner";

// NOTE `owner` is a composite role that includes `admin`, so owners count
// towards the number of admins.
function countAdminRows(rows: ClientUserRoleRow[]): number {
  return rows.filter(
    (r) => r.roles.includes("admin") || r.roles.includes(OWNER_ROLE),
  ).length;
}

interface RolesAndPermissionsProps {
  clientId: string;
  clientName?: string;
  isAdmin?: boolean;
}

export function RolesAndPermissions({
  clientId,
  clientName,
  isAdmin = false,
}: RolesAndPermissionsProps) {
  const { canExecuteAdminOperation } = useAuthOperations();
  const canExecuteOperation = useCanExecuteOperation();

  const canAddAdmin = canExecuteAdminOperation(
    operationIds["core_admin.addClientRolesToUser"],
  );

  // The owner role can only be managed by realm admins, in the admin view.
  const canManageOwnerRole = isAdmin && canAddAdmin;
  const canDeleteAdmin = canExecuteAdminOperation(
    operationIds["core_admin.removeClientRoleFromUser"],
  );
  const canListUsersAdmin = canExecuteAdminOperation(
    operationIds["core_admin.getClientUsers"],
  );
  const canListRolesAdmin = canExecuteAdminOperation(
    operationIds["core_admin.getRoles"],
  );

  const canAddCore = canExecuteOperation(
    operationIds["core.addClientRolesToUser"],
  );
  const canDeleteCore = canExecuteOperation(
    operationIds["core.removeClientRoleFromUser"],
  );
  const canListRoles = canExecuteOperation(operationIds["core.getClientRoles"]);

  const canListInvitationsAdmin = canExecuteAdminOperation(
    operationIds["core_admin.getClientInvitations"],
  );
  const canCreateInvitationAdmin = canExecuteAdminOperation(
    operationIds["core_admin.createClientInvitation"],
  );
  const canCancelInvitationAdmin = canExecuteAdminOperation(
    operationIds["core_admin.cancelClientInvitation"],
  );
  const canListInvitationsCore = canExecuteOperation(
    operationIds["core.getClientInvitations"],
  );
  const canCreateInvitationCore = canExecuteOperation(
    operationIds["core.createClientInvitation"],
  );
  const canCancelInvitationCore = canExecuteOperation(
    operationIds["core.cancelClientInvitation"],
  );

  const shouldUseAdminAPI = canListUsersAdmin;

  const canAdd = canAddAdmin || canAddCore;
  const hasAnyDeletePermission = canDeleteAdmin || canDeleteCore;
  const canEdit = canAdd && hasAnyDeletePermission;

  const canListUsers =
    shouldUseAdminAPI ||
    canExecuteOperation(operationIds["core.getClientUsers"]);

  const queryClient = useQueryClient();

  const {
    data: adminClientUsersData,
    isFetching: isFetchingAdminUsers,
    refetch: refetchAdminClientUsers,
  } = CoreQuery.useAdmin_getClientUsersQuery(clientId, {
    enabled: shouldUseAdminAPI && canListUsers,
  });

  const {
    data: clientUsersDataCore,
    isFetching: isFetchingUsersCore,
    refetch: refetchClientUsersCore,
  } = Query.useCore_getClientUsersQuery(clientId, {
    enabled: !shouldUseAdminAPI && canListUsers,
  });

  let clientUsersData: IClientUserWithRoles[] | undefined;
  let isFetchingUsers: boolean;
  let refetchGetClientUsers: (() => void) | undefined;
  if (shouldUseAdminAPI) {
    clientUsersData = adminClientUsersData;
    isFetchingUsers = isFetchingAdminUsers;
    refetchGetClientUsers = refetchAdminClientUsers;
  } else {
    clientUsersData = clientUsersDataCore;
    isFetchingUsers = isFetchingUsersCore;
    refetchGetClientUsers = refetchClientUsersCore;
  }

  const { data: adminAvailableRoles } = CoreQuery.useAdmin_getRolesQuery({
    enabled: shouldUseAdminAPI && canListRolesAdmin,
  });

  const { data: availableRolesCore } = Query.useCore_getClientRolesQuery(
    clientId,
    {
      enabled: !shouldUseAdminAPI && canListRoles,
    },
  );

  let availableRoles: IAccessRole[] | undefined;
  if (shouldUseAdminAPI) {
    availableRoles = adminAvailableRoles;
  } else {
    availableRoles = availableRolesCore;
  }

  // NOTE Only fetches the first page of invitations. Pagination not yet implemented.
  const { data: adminInvitationsData, isFetching: isFetchingAdminInvitations } =
    CoreQuery.useAdmin_getClientInvitationsQuery(
      { client_id: clientId, page: undefined, page_size: undefined },
      {
        enabled: shouldUseAdminAPI && canListInvitationsAdmin,
      },
    );

  // NOTE Only fetches the first page of invitations. Pagination not yet implemented.
  const { data: coreInvitationsData, isFetching: isFetchingCoreInvitations } =
    Query.useCore_getClientInvitationsQuery(
      { client_id: clientId, page: undefined, page_size: undefined },
      {
        enabled: !shouldUseAdminAPI && canListInvitationsCore,
      },
    );

  const { mutateAsync: addRolesAsyncAdmin, isPending: isAddingAdmin } =
    CoreQuery.useAdmin_addClientRolesToUserMutationWithParameters({
      onSettled: () => {
        if (shouldUseAdminAPI) {
          queryClient.invalidateQueries({
            queryKey: CoreQuery.admin_getClientUsersQueryKey(clientId),
          });
        }
      },
      meta: { suppressSuccess: true, suppressError: true },
    });

  const { mutateAsync: removeRoleAsyncAdmin, isPending: isRemovingAdmin } =
    CoreQuery.useAdmin_removeClientRoleFromUserMutationWithParameters({
      onSettled: () => {
        if (shouldUseAdminAPI) {
          queryClient.invalidateQueries({
            queryKey: CoreQuery.admin_getClientUsersQueryKey(clientId),
          });
        }
      },
      meta: { success: "Role removed" },
    });

  const { mutateAsync: silentRemoveRoleAsyncAdmin } =
    CoreQuery.useAdmin_removeClientRoleFromUserMutationWithParameters({
      meta: { suppressSuccess: true, suppressError: true },
    });

  const { mutateAsync: addRolesAsync, isPending: isAddingCore } =
    Query.useCore_addClientRolesToUserMutationWithParameters({
      onSettled: () => {
        if (!shouldUseAdminAPI) {
          queryClient.invalidateQueries({
            queryKey: Query.core_getClientUsersQueryKey(clientId),
          });
        }
      },
      meta: { suppressSuccess: true, suppressError: true },
    });

  const { mutateAsync: removeRoleAsyncCore, isPending: isRemovingCore } =
    Query.useCore_removeClientRoleFromUserMutationWithParameters({
      onSettled: () => {
        if (!shouldUseAdminAPI) {
          queryClient.invalidateQueries({
            queryKey: Query.core_getClientUsersQueryKey(clientId),
          });
        }
      },
      meta: { success: "Role removed" },
    });

  const { mutateAsync: silentRemoveRoleAsyncCore } =
    Query.useCore_removeClientRoleFromUserMutationWithParameters({
      meta: { suppressSuccess: true, suppressError: true },
    });

  const {
    mutateAsync: createInvitationAsyncAdmin,
    isPending: isCreatingInvitationAdmin,
  } = CoreQuery.useAdmin_createClientInvitationMutationWithParameters({
    onSettled: () => {
      if (shouldUseAdminAPI) {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getClientInvitationsQueryKey({
            client_id: clientId,
            page: undefined,
            page_size: undefined,
          }),
        });
      }
    },
    meta: {
      success:
        "Invitation email sent. The user must register for a Trustap account to access the Dashboard.",
    },
  });

  const {
    mutateAsync: cancelInvitationAsyncAdmin,
    isPending: isCancellingInvitationAdmin,
  } = CoreQuery.useAdmin_cancelClientInvitationMutationWithParameters({
    onSettled: () => {
      if (shouldUseAdminAPI) {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getClientInvitationsQueryKey({
            client_id: clientId,
            page: undefined,
            page_size: undefined,
          }),
        });
      }
    },
    meta: { success: "Invitation cancelled" },
  });

  const { mutateAsync: silentCancelInvitationAsyncAdmin } =
    CoreQuery.useAdmin_cancelClientInvitationMutationWithParameters({
      meta: { suppressSuccess: true, suppressError: true },
    });

  const {
    mutateAsync: createInvitationAsyncCore,
    isPending: isCreatingInvitationCore,
  } = Query.useCore_createClientInvitationMutationWithParameters({
    onSettled: () => {
      if (!shouldUseAdminAPI) {
        queryClient.invalidateQueries({
          queryKey: Query.core_getClientInvitationsQueryKey({
            client_id: clientId,
            page: undefined,
            page_size: undefined,
          }),
        });
      }
    },
    meta: {
      success:
        "Invitation email sent. The user must register for a Trustap account to access the Dashboard.",
    },
  });

  const {
    mutateAsync: cancelInvitationAsyncCore,
    isPending: isCancellingInvitationCore,
  } = Query.useCore_cancelClientInvitationMutationWithParameters({
    onSettled: () => {
      if (!shouldUseAdminAPI) {
        queryClient.invalidateQueries({
          queryKey: Query.core_getClientInvitationsQueryKey({
            client_id: clientId,
            page: undefined,
            page_size: undefined,
          }),
        });
      }
    },
    meta: { success: "Invitation cancelled" },
  });

  const { mutateAsync: silentCancelInvitationAsyncCore } =
    Query.useCore_cancelClientInvitationMutationWithParameters({
      meta: { suppressSuccess: true, suppressError: true },
    });

  let isAdding: boolean;
  let isRemoving: boolean;
  let removeRoleAsync: typeof removeRoleAsyncAdmin;
  let silentRemoveRoleAsync: typeof silentRemoveRoleAsyncAdmin;
  let createInvitationAsync: typeof createInvitationAsyncAdmin;
  let cancelInvitationAsync: typeof cancelInvitationAsyncAdmin;
  let silentCancelInvitationAsync: typeof silentCancelInvitationAsyncAdmin;
  let isCreatingInvitation: boolean;
  let isCancellingInvitation: boolean;
  let canCreateInvitation: boolean;
  let canCancelInvitation: boolean;
  let invitationsData: IInvitation[] | undefined;
  let isFetchingInvitations: boolean;
  if (shouldUseAdminAPI) {
    isAdding = isAddingAdmin;
    isRemoving = isRemovingAdmin;
    removeRoleAsync = removeRoleAsyncAdmin;
    silentRemoveRoleAsync = silentRemoveRoleAsyncAdmin;
    createInvitationAsync = createInvitationAsyncAdmin;
    cancelInvitationAsync = cancelInvitationAsyncAdmin;
    silentCancelInvitationAsync = silentCancelInvitationAsyncAdmin;
    isCreatingInvitation = isCreatingInvitationAdmin;
    isCancellingInvitation = isCancellingInvitationAdmin;
    canCreateInvitation = canCreateInvitationAdmin;
    canCancelInvitation = canCancelInvitationAdmin;
    invitationsData = adminInvitationsData?.data;
    isFetchingInvitations = isFetchingAdminInvitations;
  } else {
    isAdding = isAddingCore;
    isRemoving = isRemovingCore;
    removeRoleAsync = removeRoleAsyncCore;
    silentRemoveRoleAsync = silentRemoveRoleAsyncCore;
    createInvitationAsync = createInvitationAsyncCore;
    cancelInvitationAsync = cancelInvitationAsyncCore;
    silentCancelInvitationAsync = silentCancelInvitationAsyncCore;
    isCreatingInvitation = isCreatingInvitationCore;
    isCancellingInvitation = isCancellingInvitationCore;
    canCreateInvitation = canCreateInvitationCore;
    canCancelInvitation = canCancelInvitationCore;
    invitationsData = coreInvitationsData?.data;
    isFetchingInvitations = isFetchingCoreInvitations;
  }

  const pendingInvitations = useMemo(
    () =>
      (invitationsData ?? [])
        .filter((inv) => inv.status === InvitationStatus.Pending)
        .sort((a, b) => a.created_at.getTime() - b.created_at.getTime()),
    [invitationsData],
  );

  const rowsFromServer = useMemo<ClientUserRoleRow[]>(() => {
    const items = clientUsersData ?? [];
    const userRows = items
      .map((user) => {
        const userRoles = user.roles ?? [];
        const roleCodes: string[] = [];
        userRoles.forEach((roleEntry) => {
          let roleCode: string | null = null;
          if (typeof roleEntry === "string") {
            roleCode = roleEntry;
          } else if (
            roleEntry &&
            typeof roleEntry === "object" &&
            "code" in roleEntry &&
            typeof (roleEntry as { code?: string | null }).code === "string"
          ) {
            const codeValue = (roleEntry as { code?: string | null }).code;
            if (codeValue && codeValue.length > 0) {
              roleCode = codeValue;
            }
          }
          if (roleCode) {
            roleCodes.push(roleCode);
          }
        });
        if (roleCodes.length === 0) {
          return undefined;
        }
        const row: ClientUserRoleRow = {
          id: user.id,
          user_id: user.id,
          email: user.email ?? "",
          full_name: user.full_name,
          roles: roleCodes,
        };
        return row;
      })
      .filter((row): row is ClientUserRoleRow => row !== undefined);

    const invitationRows = pendingInvitations.map(
      (inv): ClientUserRoleRow => ({
        id: `inv:${inv.id}`,
        user_id: "",
        email: inv.email,
        full_name: "",
        roles: inv.roles ?? [],
        isInvitation: true,
        invitationId: inv.id,
      }),
    );

    return [...userRows, ...invitationRows];
  }, [clientUsersData, pendingInvitations]);

  const [rows, setRows] = useState<ClientUserRoleRow[]>([]);

  useEffect(() => {
    setRows(rowsFromServer);
  }, [rowsFromServer]);

  const parseRowId = useCallback((id: string) => {
    const str = String(id);
    if (str.startsWith("inv:") || str.startsWith("tmp-")) {
      return null;
    }
    if (isNonEmptyString(str)) {
      return { userId: str };
    }

    return null;
  }, []);

  const canDeleteRow = useCallback(
    (row: ClientUserRoleRow) => {
      if (!canManageOwnerRole && row.roles.includes(OWNER_ROLE)) {
        return false;
      }
      if (row.isInvitation) {
        return canCancelInvitation;
      }
      // Admin API can delete any role including last admin.
      if (canDeleteAdmin) {
        return true;
      }
      // Core API cannot delete the last admin user.
      if (canDeleteCore) {
        if (row.roles.includes("admin") && countAdminRows(rows) <= 1) {
          return false;
        }
        return true;
      }
      return false;
    },
    [
      canManageOwnerRole,
      canDeleteAdmin,
      canDeleteCore,
      canCancelInvitation,
      rows,
    ],
  );

  const canEditRow = useCallback(
    (row: ClientUserRoleRow) => {
      if (!canManageOwnerRole && row.roles.includes(OWNER_ROLE)) {
        return false;
      }
      if (row.isInvitation) {
        return canCreateInvitation;
      }
      if (!canAdd) {
        return false;
      }
      // Admin API can edit any role including last admin.
      if (canDeleteAdmin) {
        return true;
      }
      // Core API cannot edit the last admin user (since edit includes delete).
      if (canDeleteCore) {
        if (row.roles.includes("admin") && countAdminRows(rows) <= 1) {
          return false;
        }
        return true;
      }
      return false;
    },
    [
      canManageOwnerRole,
      canAdd,
      canDeleteAdmin,
      canDeleteCore,
      canCreateInvitation,
      rows,
    ],
  );

  const roleChipOptions = useMemo<ChipOption[]>(() => {
    const optionMap = new Map<string, ChipOption>();

    const toChipOption = (
      roleItem: string | IAccessRole | null | undefined,
    ) => {
      if (!roleItem) {
        return null;
      }
      if (typeof roleItem === "string") {
        return { value: roleItem, label: roleItem };
      }
      const codeValue = roleItem.code;
      const normalizedCode = codeValue ? String(codeValue) : "";
      if (normalizedCode.length === 0) {
        return null;
      }
      return {
        value: normalizedCode,
        label: roleItem.label ?? normalizedCode,
        fontColor: roleItem.font_color ?? undefined,
        backgroundColor: roleItem.background_color ?? undefined,
      };
    };

    const addOption = (roleItem: string | IAccessRole | null | undefined) => {
      const option = toChipOption(roleItem);
      if (!option) {
        return;
      }
      const existing = optionMap.get(option.value);
      if (!existing) {
        optionMap.set(option.value, option);
        return;
      }
      optionMap.set(option.value, {
        value: existing.value,
        label: existing.label || option.label,
        fontColor: existing.fontColor ?? option.fontColor,
        backgroundColor: existing.backgroundColor ?? option.backgroundColor,
      });
    };

    availableRoles?.forEach(addOption);
    (clientUsersData ?? []).forEach((user) => user.roles?.forEach(addOption));

    return Array.from(optionMap.values());
  }, [availableRoles, clientUsersData]);

  const roleChipOptionMap = useMemo(() => {
    const map = new Map<string, ChipOption>();
    roleChipOptions.forEach((option) => {
      map.set(option.value, option);
    });
    return map;
  }, [roleChipOptions]);

  const roleChipViewCell = useMemo(
    () =>
      createChipMultiSelectViewCellRenderer<ClientUserRoleRow>(
        roleChipOptionMap,
      ),
    [roleChipOptionMap],
  );

  const roleChipEditCell = useMemo(
    () =>
      createChipMultiSelectEditCellRenderer<ClientUserRoleRow>(
        canManageOwnerRole
          ? roleChipOptions
          : roleChipOptions.filter((option) => option.value !== OWNER_ROLE),
      ),
    [canManageOwnerRole, roleChipOptions],
  );

  const getRowTooltip = useCallback((row: ClientUserRoleRow) => {
    if (row.isInvitation) {
      return "The user must register for a Trustap account.";
    }
    return undefined;
  }, []);

  const getRowClassName = useCallback(
    (params: GridRowParams<ClientUserRoleRow>) => {
      if (params.row.isInvitation) {
        return "invitation-row";
      }
      return "";
    },
    [],
  );

  const columns: EditableDataGridColDef<ClientUserRoleRow>[] = useMemo(() => {
    const baseColumns: EditableDataGridColDef<ClientUserRoleRow>[] = [
      {
        field: "email",
        headerName: "User Email",
        minWidth: 354,
        flex: 1,
        editable: canEdit,
        disableColumnMenu: true,
        sortable: false,
        resizable: false,
        renderViewCell: (params: GridRenderCellParams<ClientUserRoleRow>) => (
          <Stack flexDirection="column" alignItems="flex-start">
            <Typography variant="body2">{params.value}</Typography>
            {params.row.isInvitation && (
              <Typography variant="caption" color="text.secondary">
                Invited
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "full_name",
        headerName: "User Full Name",
        minWidth: 354,
        editable: false,
        disableColumnMenu: true,
        sortable: false,
        resizable: false,
        editPlaceholder: "Auto-fills on save",
      },
    ];

    if (shouldUseAdminAPI) {
      baseColumns.push({
        field: "user_id",
        headerName: "User ID",
        minWidth: 354,
        editable: canEdit,
        disableColumnMenu: true,
        sortable: false,
        resizable: false,
      });
    }

    baseColumns.push({
      field: "roles",
      headerName: "Roles",
      editable: canEdit,
      minWidth: 220,
      disableColumnMenu: true,
      resizable: false,
      sortable: false,
      renderViewCell: roleChipViewCell,
      renderEditCell: roleChipEditCell,
      renderHeader: () => (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <span>Roles</span>
          <Tooltip
            title={
              <>
                <strong>Dashboard User Roles</strong>
                <br />
                <br />
                Roles are additive. If a user is assigned multiple roles, their
                permissions stack to provide the combined access of both.
                <br />
                <br />
                <strong>Admin:</strong> Manage Users | View Integration
                Credentials | Manage Webhooks and Redirect URIs | Manage Client
                Customisations | View Client Settings | View Transactions
                <br />
                <br />
                <strong>Reader:</strong> View Basic Client Settings | View
                Transactions
                <br />
                <br />
                <strong>Owner:</strong> Everything an Admin can do | Verify the
                Account. Only Trustap admins can assign it.
              </>
            }
          >
            <span style={{ display: "inline-flex" }}>
              <PublicSvgIcon name="info" />
            </span>
          </Tooltip>
        </Stack>
      ),
    });

    return baseColumns;
  }, [canEdit, shouldUseAdminAPI, roleChipViewCell, roleChipEditCell]);

  const createEmptyRow = useCallback((): ClientUserRoleRow => {
    const newRow: ClientUserRoleRow = {
      id: `tmp-${crypto.randomUUID()}`,
      user_id: "",
      email: "",
      full_name: "",
      roles: [],
    };
    return newRow;
  }, []);

  const handleRowsChange = useCallback((next: ClientUserRoleRow[]) => {
    setRows(next);
  }, []);

  const isLoading =
    isFetchingUsers ||
    isFetchingInvitations ||
    isAdding ||
    isRemoving ||
    isCreatingInvitation ||
    isCancellingInvitation;

  const ownerAlreadyAssignedMessage = `Client '${clientName || clientId}' already has an owner set, only one owner allowed per client.`;

  const handleSave = useCallback(
    async (row: ClientUserRoleRow) => {
      const proposedRoles = row.roles;

      const isTemp = String(row.id).startsWith("tmp-");
      if (isTemp) {
        try {
          if (shouldUseAdminAPI) {
            await addRolesAsyncAdmin({
              client_id: clientId,
              addClientRolesToUserBody: AddClientRolesToUserBody.fromJS({
                user_id: row.user_id || undefined,
                email: row.email ? String(row.email) : undefined,
                roles: proposedRoles,
              }),
            });
          } else {
            await addRolesAsync({
              client_id: clientId,
              addClientRolesToUserBody: AddClientRolesToUserBody2.fromJS({
                email: String(row.email),
                roles: proposedRoles,
              }),
            });
          }
          enqueueSnackbar(
            proposedRoles.length === 1
              ? "User role assigned. An invitation email has been sent to the user's inbox."
              : "User roles assigned. An invitation email has been sent to the user's inbox.",
            { variant: "success" },
          );
        } catch (err: unknown) {
          const error = err as { response?: { code?: string } };
          if (error?.response?.code === ERROR_CODE_OWNER_ALREADY_ASSIGNED) {
            enqueueSnackbar(ownerAlreadyAssignedMessage, { variant: "error" });
          } else if (
            error?.response?.code === ERROR_CODE_USER_NOT_FOUND &&
            canCreateInvitation
          ) {
            const email = String(row.email).trim().toLowerCase();
            // NOTE If a pending invitation already exists for this email, cancel
            // it and create a new one with the combined roles so that all roles
            // are captured in a single invitation.
            const existingInvite = pendingInvitations.find(
              (inv) => (inv.email ?? "").trim().toLowerCase() === email,
            );
            const combinedRoles = existingInvite
              ? [
                  ...new Set([
                    ...(existingInvite.roles ?? []),
                    ...proposedRoles,
                  ]),
                ]
              : proposedRoles;
            if (existingInvite?.id) {
              await silentCancelInvitationAsync({
                client_id: clientId,
                invitation_id: existingInvite.id,
              });
            }
            if (shouldUseAdminAPI) {
              await createInvitationAsync({
                client_id: clientId,
                createClientInvitationBody: CreateClientInvitationBody.fromJS({
                  email: row.email ? String(row.email) : "",
                  roles: combinedRoles,
                }),
              });
            } else {
              await createInvitationAsync({
                client_id: clientId,
                createClientInvitationBody: CreateClientInvitationBody2.fromJS({
                  email: String(row.email),
                  roles: combinedRoles,
                }),
              });
            }
          } else {
            throw err;
          }
        }
        return;
      }

      // NOTE Editing an invitation row: cancel the existing invitation and
      // create a new one with the updated roles.
      if (row.isInvitation && row.invitationId) {
        await silentCancelInvitationAsync({
          client_id: clientId,
          invitation_id: row.invitationId,
        });
        if (shouldUseAdminAPI) {
          await createInvitationAsync({
            client_id: clientId,
            createClientInvitationBody: CreateClientInvitationBody.fromJS({
              email: row.email ? String(row.email) : "",
              roles: proposedRoles,
            }),
          });
        } else {
          await createInvitationAsync({
            client_id: clientId,
            createClientInvitationBody: CreateClientInvitationBody2.fromJS({
              email: String(row.email),
              roles: proposedRoles,
            }),
          });
        }
        return;
      }

      const parsed = parseRowId(row.id);
      if (!parsed) {
        throw new Error("Invalid row identifier");
      }
      const existingUserId = parsed.userId;

      const proposedUserId = row.user_id?.trim() || "";
      const proposedEmail = row.email ? String(row.email).trim() : "";

      const originalUser = (clientUsersData ?? []).find(
        (u: { id: string; email?: string | null }) => u.id === existingUserId,
      );
      const originalEmail = (originalUser?.email ?? "").trim().toLowerCase();
      const originalRoles = (originalUser?.roles ?? [])
        .map((r) =>
          typeof r === "string" ? r : (r as { code?: string })?.code,
        )
        .filter((r): r is string => !!r);

      const hasUserIdChanged =
        shouldUseAdminAPI &&
        proposedUserId.length > 0 &&
        proposedUserId !== existingUserId;
      const hasEmailChanged =
        proposedEmail.length > 0 &&
        proposedEmail.toLowerCase() !== originalEmail;
      const rolesToAdd = proposedRoles.filter(
        (r) => !originalRoles.includes(r),
      );
      const rolesToRemove = originalRoles.filter(
        (r) => !proposedRoles.includes(r),
      );
      const hasRolesChanged = rolesToAdd.length > 0 || rolesToRemove.length > 0;

      if (!hasUserIdChanged && !hasRolesChanged && !hasEmailChanged) {
        return;
      }

      let targetUserId: string | null = null;
      let targetEmail: string | null = null;

      if (hasUserIdChanged || hasEmailChanged) {
        targetUserId = proposedUserId;
        targetEmail = proposedEmail;
      } else {
        targetUserId = existingUserId;
      }

      // When the user identity changed (email or userId), we must move all
      // roles to the new target: add all proposed roles to the new user,
      // then remove all original roles from the old user.
      const isUserIdentityChange = hasUserIdChanged || hasEmailChanged;
      const effectiveRolesToAdd = isUserIdentityChange
        ? proposedRoles
        : rolesToAdd;
      const effectiveRolesToRemove = isUserIdentityChange
        ? originalRoles
        : rolesToRemove;

      try {
        if (effectiveRolesToAdd.length > 0) {
          if (shouldUseAdminAPI) {
            await addRolesAsyncAdmin({
              client_id: clientId,
              addClientRolesToUserBody: AddClientRolesToUserBody.fromJS({
                user_id: targetUserId,
                email: targetEmail,
                roles: effectiveRolesToAdd,
              }),
            });
          } else {
            await addRolesAsync({
              client_id: clientId,
              addClientRolesToUserBody: AddClientRolesToUserBody2.fromJS({
                email: targetEmail || originalEmail,
                roles: effectiveRolesToAdd,
              }),
            });
          }
        }
        await Promise.allSettled(
          effectiveRolesToRemove.map((roleToRemove) =>
            removeRoleAsync({
              client_id: clientId,
              user_id: existingUserId,
              role_name: roleToRemove,
            }),
          ),
        );
        enqueueSnackbar(
          proposedRoles.length === 1
            ? "User role assigned. An invitation email has been sent to the user's inbox."
            : "User roles assigned. An invitation email has been sent to the user's inbox.",
          { variant: "success" },
        );
      } catch (err) {
        const error = err as { response?: { code?: string } };
        if (error?.response?.code === ERROR_CODE_OWNER_ALREADY_ASSIGNED) {
          enqueueSnackbar(ownerAlreadyAssignedMessage, { variant: "error" });
        } else {
          console.error("Failed to edit roles:", err);
        }
      }
    },
    [
      shouldUseAdminAPI,
      addRolesAsyncAdmin,
      addRolesAsync,
      removeRoleAsync,
      createInvitationAsync,
      silentCancelInvitationAsync,
      canCreateInvitation,
      pendingInvitations,
      clientId,
      clientUsersData,
      parseRowId,
      ownerAlreadyAssignedMessage,
    ],
  );

  const handleDelete = useCallback(
    async (row: ClientUserRoleRow) => {
      const isTemp = String(row.id).startsWith("tmp-");
      if (isTemp) {
        return;
      }

      if (row.isInvitation && row.invitationId) {
        const email = row.email ? String(row.email).trim().toLowerCase() : "";
        const matchedUser = (clientUsersData ?? []).find(
          (u) => (u.email ?? "").trim().toLowerCase() === email,
        );

        await Promise.allSettled([
          cancelInvitationAsync({
            client_id: clientId,
            invitation_id: row.invitationId,
          }),
          ...(matchedUser
            ? row.roles.map((role) =>
                silentRemoveRoleAsync({
                  client_id: clientId,
                  user_id: matchedUser.id,
                  role_name: role,
                }),
              )
            : []),
        ]);
        refetchGetClientUsers?.();
        return;
      }

      const parsed = parseRowId(row.id);
      if (!parsed) {
        return;
      }

      // Delete all roles for this user.
      await Promise.allSettled(
        row.roles.map((role) =>
          removeRoleAsync({
            client_id: clientId,
            user_id: parsed.userId,
            role_name: role,
          }),
        ),
      );
      refetchGetClientUsers?.();
    },
    [
      clientId,
      clientUsersData,
      removeRoleAsync,
      silentRemoveRoleAsync,
      cancelInvitationAsync,
      parseRowId,
      refetchGetClientUsers,
    ],
  );

  return (
    <Stack>
      <EditableDataGrid<ClientUserRoleRow>
        dataGridTestId="roles-permissions-grid"
        addButtonDataTestId="add-user-role-button"
        columns={columns}
        rows={rows}
        onRowsChange={handleRowsChange}
        createEmptyRow={createEmptyRow}
        loading={isLoading}
        headingIconName="manage_accounts"
        canAdd={canAdd}
        canEdit={canEditRow}
        canDelete={canDeleteRow}
        heading="Roles & Permissions"
        addButtonLabel="Add User & Role"
        zodSchema={clientUserRoleRowSchema}
        onSave={handleSave}
        onDelete={handleDelete}
        noRowsLabel="No Roles & Permissions"
        deleteDialogHeading="Delete Roles"
        getDeleteDialogMessage={(row) => {
          const roleLabels = row.roles.map(
            (r) => roleChipOptionMap.get(r)?.label ?? r,
          );
          if (roleLabels.length === 1) {
            return `Are you sure you want to remove ${row.email} from this client? This will delete their ${roleLabels[0]} role.\n\nNote: It may take up to 5 minutes for changes to take effect.`;
          }
          return `Are you sure you want to remove ${row.email} from this client? This will delete their ${roleLabels.join(", ")} roles.\n\nNote: It may take up to 5 minutes for changes to take effect.`;
        }}
        deleteDialogConfirmText="Yes, delete"
        deleteDialogCancelText="No, cancel"
        getRowClassName={getRowClassName}
        getRowTooltip={getRowTooltip}
        shouldConfirmDelete={(row) => !row.isInvitation}
      />
    </Stack>
  );
}
